import React, { useEffect, useState } from "react";
import { FileText, Loader2, Plus } from "lucide-react";
import { Card, CardContent, CardFooter } from "../ui/card";
import { Button } from "../ui/button";
import TemplateCreatorModal from "../TemplateCreatorModal";
import { ColumnSelect } from "../ui/select";
import {
  buildCampaignTemplateName,
  DEFAULT_TEMPLATE_LANGUAGE,
  DEFAULT_TEMPLATE_TYPE,
  normalizeMetaTemplateName,
} from "@/lib/metaTemplateUtils";
import { toast } from "sonner";

interface Props {
  templateId: string | null;
  setTemplateId: (v: string | null) => void;
  templates: any[];
  showTemplateModal: boolean;
  setShowTemplateModal: (v: boolean) => void;
  handleTemplateCreated: (id?: string) => void;
  templateType: string;
  setTemplateType: (v: string) => void;
  templateLanguage: string;
  setTemplateLanguage: (v: string) => void;
  templateNameInput: string;
  setTemplateNameInput: (v: string) => void;
  setMessageTemplate?: (v: string) => void;
  /** Campaign name used to auto-build `{name}_plantilla` when creating a new template. */
  campaignName?: string;
  /** True while Meta/local templates are still being fetched. */
  isLoadingTemplates?: boolean;
  campaignType?: string;
  sendDate?: string;
  sendTime?: string;
  onBack: () => void;
  onNext: () => void;
  hideNavigation?: boolean;
}

const StepTemplate: React.FC<Props> = ({
  templateId, setTemplateId, templates, showTemplateModal, setShowTemplateModal, handleTemplateCreated,
  onBack, onNext,
  templateType, setTemplateType, templateLanguage, setTemplateLanguage, templateNameInput, setTemplateNameInput,
  setMessageTemplate,
  campaignName = "",
  isLoadingTemplates = false,
  campaignType, sendDate, sendTime,
  hideNavigation
}) => {
  const isMarketingCampaign = campaignType === "Marketing";
  const [selectedTemplateName, setSelectedTemplateName] = useState<string | null>(null);
  const [templateError, setTemplateError] = useState(false);
  const [timeError, setTimeError] = useState(false);

  useEffect(() => {
    try {
      document.querySelectorAll(
        '.introjs-overlay, .introjs-helperLayer, .introjs-tooltipReferenceLayer'
      ).forEach(el => el.remove());
      document.body.classList.remove('introjs-fixParent', 'introjs-noScroll');
    } catch { }
  }, []);

  const hasApprovedTemplates = !isLoadingTemplates && templates && templates.length > 0;
  const showCreateEmpty = !isLoadingTemplates && !hasApprovedTemplates;

  useEffect(() => {
    try {
      const found = templates.find((t: any) => String(t.id ?? t._id) === String(templateId));
      if (found) {
        const displayName = found.displayName ?? found.name?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) ?? found.title ?? `Plantilla ${found.id ?? found._id}`;
        setSelectedTemplateName(displayName);
      } else {
        setSelectedTemplateName(null);
      }
    } catch {
      setSelectedTemplateName(null);
    }
  }, [templateId, templates]);

  useEffect(() => {
    if (isLoadingTemplates || !hasApprovedTemplates) return;

    const isRenewal = campaignType !== 'Marketing' && campaignType !== 'Dynamic';
    const tourKey = isRenewal ? 'renewal_tour_step3_seen' : 'marketing_tour_step3_seen';

    const triggerStage3 = () => {
      return setTimeout(() => {
        if (isRenewal) {
          import('@/components/common/RenewalCampaignTour').then(({ startRenewalStep3Tour }) => {
            startRenewalStep3Tour(() => sessionStorage.setItem(tourKey, 'true'));
          }).catch(e => console.error('Error cargando Renewal Step3 Tour:', e));
        } else if (campaignType === 'Dynamic') {
          import('@/components/common/DynamicCampaignTour').then(({ startDynamicStep3Tour }) => {
            startDynamicStep3Tour(() => sessionStorage.setItem(tourKey, 'true'));
          }).catch(e => console.error('Error cargando Dynamic Step3 Tour:', e));
        } else {
          import('@/components/common/MarketingCampaignTour').then(({ startMarketingStep3Tour }) => {
            startMarketingStep3Tour(() => sessionStorage.setItem(tourKey, 'true'));
          }).catch(e => console.error('Error cargando Marketing Step3 Tour:', e));
        }
      }, 300);
    };

    let timeoutId: NodeJS.Timeout | undefined;

    const hasSeen = sessionStorage.getItem(tourKey);
    if (!hasSeen) {
      timeoutId = triggerStage3();
    }

    const handleRestart = (e: Event) => {
      if (e instanceof CustomEvent && e.detail) e.detail.handled = true;
      sessionStorage.removeItem(tourKey);
      timeoutId = triggerStage3();
    };

    window.addEventListener('restart-local-tour', handleRestart);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener('restart-local-tour', handleRestart);
    };
  }, [isLoadingTemplates, hasApprovedTemplates, campaignType]);

  // Tour when there are no approved templates (empty state + create CTA)
  useEffect(() => {
    if (isLoadingTemplates || hasApprovedTemplates) return;

    const isRenewal = campaignType !== 'Marketing' && campaignType !== 'Dynamic';
    const tourKey = isRenewal ? 'renewal_tour_create_template_seen' : 'marketing_tour_create_template_seen';

    const triggerCreateTour = () => {
      return setTimeout(() => {
        if (isRenewal) {
          import('@/components/common/RenewalCampaignTour').then(({ startRenewalCreateTemplateTour }) => {
            startRenewalCreateTemplateTour(() => sessionStorage.setItem(tourKey, 'true'));
          }).catch(e => console.error('Error cargando Renewal Create Template Tour:', e));
        } else if (campaignType === 'Dynamic') {
          import('@/components/common/DynamicCampaignTour').then(({ startDynamicCreateTemplateTour }) => {
            startDynamicCreateTemplateTour(() => sessionStorage.setItem(tourKey, 'true'));
          }).catch(e => console.error('Error cargando Dynamic Create Template Tour:', e));
        } else {
          import('@/components/common/MarketingCampaignTour').then(({ startMarketingCreateTemplateTour }) => {
            startMarketingCreateTemplateTour(() => sessionStorage.setItem(tourKey, 'true'));
          }).catch(e => console.error('Error cargando Marketing Create Template Tour:', e));
        }
      }, 700);
    };

    let timeoutId: NodeJS.Timeout | undefined;

    const hasSeen = sessionStorage.getItem(tourKey);
    if (!hasSeen) {
      timeoutId = triggerCreateTour();
    }

    const handleRestart = (e: Event) => {
      if (e instanceof CustomEvent && e.detail) e.detail.handled = true;
      sessionStorage.removeItem(tourKey);
      timeoutId = triggerCreateTour();
    };

    window.addEventListener('restart-local-tour', handleRestart);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener('restart-local-tour', handleRestart);
    };
  }, [isLoadingTemplates, hasApprovedTemplates, campaignType]);

  const getDisplayName = (t: any) => t.displayName ?? t.name?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) ?? t.title ?? `Plantilla ${t.id ?? t._id}`;

  const templateOptions = templates.map((t: any) => getDisplayName(t));

  const onTemplateSelect = (value: string | null) => {
    setSelectedTemplateName(value);
    const t = templates.find((tpl: any) => getDisplayName(tpl) === value);
    if (t) {
      setTemplateId(t.id ?? t._id);
      if (t.category) {
        const categoryMap: Record<string, string> = {
          'MARKETING': 'Marketing: Promociones y ofertas',
          'UTILITY': DEFAULT_TEMPLATE_TYPE,
        };
        setTemplateType(categoryMap[t.category] || DEFAULT_TEMPLATE_TYPE);
      }
      if (t.language) {
        const langCode = t.language.toLowerCase().includes('es') ? 'Español' :
          t.language.toLowerCase().includes('en') ? 'Inglés' : 'Español';
        setTemplateLanguage(langCode);
      }
      setTemplateNameInput("");
    } else {
      setTemplateId(null);
    }
  };

  const isScheduledTimeValid = () => {
    if (!isMarketingCampaign) return true;
    if (!sendDate || !sendTime) return true;
    const scheduledDateTime = new Date(`${sendDate}T${sendTime}:00`);
    const now = new Date();
    const hoursUntilSend = (scheduledDateTime.getTime() - now.getTime()) / (1000 * 60 * 60);
    return hoursUntilSend >= 24;
  };

  const isDuplicateTemplateName = (displayName: string) => {
    const normalizedInput = normalizeMetaTemplateName(displayName);
    return templates.some(t => {
      const tName = normalizeMetaTemplateName(t.name || t.meta_template_name || "");
      return tName === normalizedInput;
    });
  };

  /** Auto-fill Meta-required fields and advance to message composition. */
  const startCreateTemplateFlow = () => {
    if (isLoadingTemplates) return;
    setTimeError(false);

    if (!(campaignName || "").trim()) {
      toast.error("Introduce un nombre para la campaña antes de crear la plantilla.");
      return;
    }

    const autoName = buildCampaignTemplateName(campaignName);
    if (isDuplicateTemplateName(autoName)) {
      toast.error(
        `Ya existe una plantilla llamada "${normalizeMetaTemplateName(autoName)}". Cambia el nombre de la campaña o selecciona una plantilla existente.`
      );
      return;
    }

    if (!isScheduledTimeValid()) {
      setTimeError(true);
      return;
    }

    setSelectedTemplateName(null);
    setTemplateId(null);
    setTemplateError(false);
    setTemplateNameInput(autoName);
    setTemplateType(DEFAULT_TEMPLATE_TYPE);
    setTemplateLanguage(DEFAULT_TEMPLATE_LANGUAGE);
    if (setMessageTemplate) {
      setMessageTemplate("");
    }
    onNext();
  };

  const handleNext = () => {
    if (isLoadingTemplates) return;
    if (hasApprovedTemplates) {
      if (!selectedTemplateName && !templateId) {
        setTemplateError(true);
        return;
      }
      onNext();
      return;
    }
    // No approved templates: create path with auto metadata
    startCreateTemplateFlow();
  };

  const title = isLoadingTemplates
    ? "Plantilla (WhatsApp)"
    : hasApprovedTemplates
      ? "Selección plantilla de mensaje (WhatsApp)"
      : "Creación plantilla de mensaje (WhatsApp)";

  const subtitle = isLoadingTemplates
    ? "Cargando tus plantillas aprobadas…"
    : hasApprovedTemplates
      ? "Selecciona una plantilla aprobada por Meta o crea una nueva para tu campaña."
      : "Se creará una plantilla automáticamente con el nombre de tu campaña.";

  return (
    <Card className="mt-6 relative">
      <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl bg-gradient-to-b from-[#1CB3B3] to-[#179999]" />
      <CardContent className="space-y-6 p-4 pl-5 sm:p-6 sm:pl-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex-none bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] text-white flex items-center justify-center">
            <FileText className="w-5 h-5" />
          </div>
          <div>
            <div className="text-lg font-semibold text-gray-900">{title}</div>
            <p className="text-xs text-gray-400">{subtitle}</p>
          </div>
        </div>

        {isLoadingTemplates && (
          <div className="flex flex-col items-center justify-center gap-3 py-12 text-gray-500">
            <Loader2 className="w-8 h-8 animate-spin text-[#1CB3B3]" />
            <p className="text-sm font-medium">Cargando plantillas…</p>
          </div>
        )}

        {showCreateEmpty && (
          <div id="tour-marketing-template-approval-warning" className="rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-4 sm:p-5 shadow-sm">
            <div className="flex flex-col sm:flex-row items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-amber-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
              </div>
             <div className="min-w-0 flex-1">
                <div className="font-semibold text-gray-900 mb-2">Importante: Tiempo de aprobación</div>
                <p className="text-sm text-gray-600 mb-3">
                  Las plantillas nuevas deben ser aprobadas por Meta antes de enviarse. Este proceso puede tomar hasta 24 horas.
                </p>
                <div className="flex items-start gap-2">
                  <span className="text-amber-500 text-sm font-bold mt-0.5">!</span>
                  <p className="text-sm text-gray-700">
                    Deberás programar el envío de la campaña para <span className="font-bold text-amber-700">al menos 24 horas después de este momento</span>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {hasApprovedTemplates && (
          <>
            <div id="tour-marketing-template-select" className="rounded-xl border border-[#1CB3B3]/20 bg-[#1CB3B3]/5 p-5 shadow-sm">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-emerald-600 text-lg">✓</span>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Plantillas aprobadas disponibles</div>
                  <p className="text-sm text-gray-500 mt-0.5">Selecciona una de tus plantillas ya aprobadas por Meta para envíos de WhatsApp.</p>
                </div>
              </div>

              <div className="mt-4">
                <ColumnSelect
                  label="Seleccionar plantilla de mensaje"
                  options={templateOptions}
                  value={selectedTemplateName}
                  onChange={(v) => { onTemplateSelect(v); setTemplateError(false); }}
                  placeholder="-- Elige una plantilla aprobada --"
                />
                {templateError && <p className="text-xs text-rose-600 mt-2">Selecciona una plantilla para continuar</p>}
              </div>
            </div>

            <div className="flex items-center justify-center py-4">
              <div className="hidden sm:block flex-1 h-px bg-gray-200"></div>
              <button
                type="button"
                id="tour-marketing-template-create"
                onClick={startCreateTemplateFlow}
                className="mx-0 sm:mx-4 w-full sm:w-auto min-w-0 inline-flex items-center justify-center gap-2 px-3 sm:px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#1CB3B3] to-[#179999] text-sm font-semibold text-white text-center whitespace-normal leading-tight shadow-[0_4px_20px_rgba(28,179,179,0.4)] hover:shadow-[0_6px_25px_rgba(28,179,179,0.5)] transition-all animate-pulse hover:animate-none"
              >
                <Plus className="w-4 h-4 shrink-0" />
                ¿Deseas crear una nueva plantilla de mensaje?
              </button>
              <div className="hidden sm:block flex-1 h-px bg-gray-200"></div>
            </div>
          </>
        )}

        {showCreateEmpty && (
          <div className="flex items-center justify-center py-2">
            <button
              type="button"
              id="tour-marketing-template-create"
              onClick={startCreateTemplateFlow}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#1CB3B3] to-[#179999] text-sm font-semibold text-white shadow-[0_4px_20px_rgba(28,179,179,0.4)] hover:shadow-[0_6px_25px_rgba(28,179,179,0.5)] transition-all"
            >
              <Plus className="w-4 h-4" />
              Crear plantilla y continuar
            </button>
          </div>
        )}

        {timeError && !isLoadingTemplates && (
          <div className="mt-4 rounded-xl border-2 border-rose-300 bg-gradient-to-r from-rose-50 to-red-50 p-5 shadow-sm animate-pulse">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-rose-100 flex items-center justify-center flex-shrink-0">
                <span className="text-rose-600 text-lg">⚠️</span>
              </div>
              <div className="flex-1">
                <div className="font-bold text-rose-800 mb-2">Hora de envío no válida para plantilla nueva</div>
                <p className="text-sm text-rose-700 mb-3">
                  Para crear una nueva plantilla, debes programar el envío para <span className="font-bold">al menos 24 horas después de este momento</span>.
                  Esto le da tiempo a Meta para aprobar tu plantilla antes del envío.
                </p>
                <div className="flex items-center gap-2 p-3 bg-white/60 rounded-lg border border-rose-200">
                  <span className="text-rose-500 font-bold">→</span>
                  <p className="text-sm text-rose-800 font-medium">
                    Ve atrás al paso anterior, modifica tu hora de envío para que sea posterior a 24 horas, y luego vuelve aquí para continuar.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

      </CardContent>
      {!hideNavigation && (
        <CardFooter className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 bg-gray-50/80 px-4 sm:px-8 py-5 border-t border-gray-100 mt-2 rounded-b-xl">
          <Button
            variant="outline"
            onClick={onBack}
            className="w-full sm:w-auto border-gray-300 text-gray-600 hover:bg-gray-50 px-6 h-11 rounded-xl shadow-sm bg-white"
          >
            ← Atrás
          </Button>
          <Button
            onClick={handleNext}
            disabled={isLoadingTemplates}
            className="w-full sm:w-auto bg-gradient-to-r from-[#1CB3B3] to-[#179999] hover:from-[#179999] hover:to-[#1CB3B3] text-white font-semibold px-8 h-11 rounded-xl shadow-lg hover:shadow-cyan-500/30 transform transition-all active:scale-95 disabled:opacity-50 disabled:pointer-events-none"
          >
            Siguiente →
          </Button>
        </CardFooter>
      )}
      <TemplateCreatorModal isOpen={showTemplateModal} onClose={() => setShowTemplateModal(false)} onCreated={handleTemplateCreated} />
    </Card>
  );
}

export default StepTemplate;
